import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../loaders';
import { Button, ButtonGroup, Col, Container, Row } from 'reactstrap';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import api from "../../../services/api";
import { ErrorNotification } from '../../alerts';
import ConfirmGeneralAlert from '../../alerts/ConfirmGeneralAlert';
import CandidateSelector from './CandidateSelector';
import ECCBaseForm from './ECCF/ECCBaseForm';
import TerminationBaseForm from './Termination/TerminationBaseForm';
import ECCApprovalPanel from './ECCF/ECCApprovalPanel';
import { checkApprover as checkApproverEcc, composeDataToSend as composeDataToSendEcc, returnToList as returnToListEcc, validate as validateEcc } from './ECCF/ECCUtils';
import { TooltipButton } from '../../inputs';
import { checkApprover as checkApproverTermination, composeDataToSend as composeDataToSendTermination, returnToList as returnToListTermination, validate as validateTermination } from './Termination/TerminationUtils';
import TerminationApprovalPanel from './Termination/TerminationApprovalPanel';
import { useMsal } from '@azure/msal-react';
import _ from 'lodash';
import { roles } from '../../../auth/roles';

const eccApiUrl = "employeechangeconditionv2";
const eccFormType = "ecc";
const terminationApiUrl = "termination";
const terminationFormType = "exit";

export default function InitialForm() {
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [eccLookups, setEccLookups] = useState({});
    const [terminationLookups, setTerminationLookups] = useState({});
    const [reloadEccf, setReloadEccf] = useState(true);
    const [reloadTermination, setReloadTermination] = useState(true);
    const [formType, setFormType] = useState(eccFormType);
    const [selectedCandidate, setSelectedCandidate] = useState({});
    const [employmentType, setEmploymentType] = useState(null);
    const [refreshItemInEditForHistory, setRefreshItemInEditForHistory] = useState(false);
    const [selectedAllocationHistoryRows, setSelectedAllocationHistoryRows] = useState([]);
    const [itemInEditEccf, setItemInEditEccf] = useState({});
    const [itemInEditTermination, setItemInEditTermination] = useState({});
    const [documents, setDocuments] = useState([]);
    const [shouldCheckApprover, setShouldCheckApprover] = useState(false);
    const [loadingCheckApprover, setLoadingCheckApprover] = useState(false);
    const [terminationApproverChecked, setTerminationApproverChecked] = useState(true);
    const [validationObj, setValidationObj] = useState([]);
    const [showValidation, setShowValidation] = useState(false);
    const [initialApprover, setInitialApprover] = useState(null);
    const [isApproverExists, setIsApproverExists] = useState(false);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const [canSave, setCanSave] = useState(false);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const history = useHistory();

    const hasFullAccess = useMemo(() => {
        return _.intersection([roles.HR], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const scrollToValidation = useCallback(() => {
        if (!validationObj?.length)
            return;

        setTimeout(function () { //Start the timer
            const scrollObject = document.getElementById("validationAlert");
            if (!scrollObject) return;
            scrollObject.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }.bind(this), 500);
    }, [validationObj]);

    useEffect(() => {
        setItemInEditEccf({
            ...itemInEditEccf,
            employmentTypeId: employmentType?.employmentTypeId,
            employmentType: employmentType,
            candidateId: selectedCandidate?.candidateId,
            candidate: selectedCandidate?.candidate,
        });

        setItemInEditTermination({
            ...itemInEditTermination,
            employmentTypeId: employmentType?.employmentTypeId,
            employmentType: employmentType,
            candidateId: selectedCandidate?.candidateId,
            candidate: selectedCandidate?.candidate,
        });
    }, [employmentType]);

    useEffect(() => {
        if (!shouldCheckApprover)
            return;

        setShouldCheckApprover(false);
        setValidationObj([]);

        if (formType === eccFormType)
        {
            checkApproverEcc(itemInEditEccf, setItemInEditEccf, setInitialApprover, setIsApproverExists, validationObj, setValidationObj, user, api, eccApiUrl, setLoadingCheckApprover, _);
            return;
        }

        if (formType === terminationFormType)
        {
            setTerminationApproverChecked(true);
            checkApproverTermination(itemInEditTermination, setItemInEditTermination, setInitialApprover, setIsApproverExists, validationObj, setValidationObj, user, api, terminationApiUrl, setLoadingCheckApprover, _, () => setTerminationApproverChecked(false));
            return;
        }
    }, [shouldCheckApprover]);

    useEffect(() => {
        if (!refreshItemInEditForHistory)
            return;

        setRefreshItemInEditForHistory(false);
        setItemInEditEccf({
            ...itemInEditEccf,
            projectId: selectedAllocationHistoryRows[0]?.original?.projectId ?? null,
            project: selectedAllocationHistoryRows[0]?.original?.project ?? null,
            positionId: selectedAllocationHistoryRows[0]?.original?.positionId ?? null,
            position: selectedAllocationHistoryRows[0]?.original?.position ?? null,
            currentEndDate: selectedAllocationHistoryRows[0]?.original?.endDate ?? null,
            proposedEndDate: selectedAllocationHistoryRows[0]?.original?.endDate ? itemInEditEccf.proposedEndDate : null,
        });
        setItemInEditTermination({
            ...itemInEditTermination,
            projectId: selectedAllocationHistoryRows[0]?.original?.projectId ?? null,
            project: selectedAllocationHistoryRows[0]?.original?.project ?? null,
            positionId: selectedAllocationHistoryRows[0]?.original?.positionId ?? null,
            position: selectedAllocationHistoryRows[0]?.original?.position ?? null,
            companyId: selectedAllocationHistoryRows[0]?.original?.companyId ?? null,
            company: selectedAllocationHistoryRows[0]?.original?.company ?? null,
        });
        setShouldCheckApprover(true);
    }, [refreshItemInEditForHistory]);

    useEffect(() => {
        setShouldCheckApprover(true);
    }, [formType]);

    useEffect(() => {
        if (!showValidation)
            return;

        setShowValidation(false);
        scrollToValidation();
    }, [showValidation]);

    const returnToHome = useCallback(() => {
        history.push("/");
    }, [history]);

    const handleSaveEcc = useCallback((readyForApproval = false) => {
        setLoading(true);
        const validationResult = validateEcc(itemInEditEccf, documents, readyForApproval);
        setValidationObj([...validationResult]);

        if (validationResult?.length)
        {
            setShowValidation(true);
            setLoading(false);
            return;
        }
        
        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${eccApiUrl}?readyForApproval=${readyForApproval}`;
        const method = "POST";
        const save = api({ url, method, data: composeDataToSendEcc(null, itemInEditEccf, documents) });
        
        save
            .then(() => {
                setLoading(false);

                if (hasFullAccess)
                {
                    returnToListEcc(history);
                    return;
                }

                returnToHome();
            })
            .catch(error => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: `${error.response.data} Your data has not been saved.`,
                });
                setLoading(false);
            })
        ;
    }, [itemInEditEccf, documents, hasFullAccess]);

    const handleSaveTermination = useCallback((submitApproval = false) => {
        const validationResult = validateTermination(itemInEditTermination, false, false);

        if (submitApproval && !itemInEditTermination?.approverName && !itemInEditTermination?.autoApproved)
            validationResult.push({
                type: "error",
                message: "Approver has not been set. Please contact your administrator.",
            });

        setValidationObj([...validationResult]);

        if (validationResult?.length)
        {
            setShowValidation(true);
            return;
        }

        setLoading(true);
        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${terminationApiUrl}`;
        const method = "POST";

        api({
            url,
            method,
            data: composeDataToSendTermination(itemInEditTermination, submitApproval),
        })
            .then(() => {
                setLoading(false);

                if (hasFullAccess)
                {
                    returnToListTermination(history);
                    return;
                }

                returnToHome();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [itemInEditTermination, canSave, hasFullAccess]);

    const eccFooter = useMemo(() => {
        return (
            <Row className="m-0 p-0 mt-3">
                <Col xs={12} className="d-flex justify-content-between m-0 p-0 mb-4">
                    <Row className="w-100 m-0 p-0">
                        <Col xs={3} className="d-flex flex-wrap justify-content-start align-items-center m-0 p-0">
                            <Button
                                color="info"
                                onClick={() => returnToListEcc(history)}
                                type="button"
                            >
                                Back
                            </Button>
                        </Col>
                        <Col xs={9} className="d-flex flex-wrap justify-content-end align-items-center m-0 p-0">
                        {
                            hasFullAccess ?
                            <Button
                                color="default"
                                onClick={() => handleSaveEcc()}
                                type="button"
                            >
                                Save
                            </Button>
                            :
                            null
                        }
                            <Button
                                color="default"
                                onClick={() => {
                                    setLoading(true);
                                    const validationResult = validateEcc(itemInEditEccf, documents, true);
                                    setValidationObj([...validationResult]);

                                    if (validationResult?.length)
                                    {
                                        setShowValidation(true);
                                        setLoading(false);
                                        return;
                                    }

                                    setLoading(false);
                                    setPopupMessageObj({
                                        type: "confirmation",
                                        message: "Are you sure you want to submit this request for approval?",
                                    });
                                }}
                                type="button"
                            >
                                Save and Submit Approval
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    }, [itemInEditEccf, history, handleSaveEcc]);

    const terminationFooter = useMemo(() => {
        return (
            <Row>
                <Col xs={12} className="d-flex flex-wrap justify-content-end">
                    <Row className="w-100 m-0 p-0 mb-4">
                        <Col xs={3} className="d-flex flex-wrap justify-content-start align-items-center m-0 p-0">
                            <Button
                                color="info"
                                type="button"
                                onClick={() => returnToListTermination(history)}
                            >
                                Close
                            </Button>
                        </Col>
                        <Col xs={9} className="d-flex flex-wrap justify-content-end align-items-center m-0 p-0">
                        {
                            hasFullAccess ?
                            <Button
                                color="default"
                                type="button"
                                onClick={() => handleSaveTermination()}
                            >
                                Save
                            </Button>
                            :
                            null
                        }
                            <Button
                                color="default"
                                type="button"
                                onClick={() => {
                                    setLoading(true);
                                    const validationResult = validateTermination(itemInEditTermination, false, false);
                                    setValidationObj([...validationResult]);

                                    if (validationResult?.length)
                                    {
                                        setShowValidation(true);
                                        setLoading(false);
                                        return;
                                    }

                                    setLoading(false);
                                    setPopupMessageObj({
                                        type: "confirmation",
                                        message: "Are you sure you want to submit this request for approval?",
                                    });
                                }}
                            >
                                Save and Submit Approval
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    }, [itemInEditTermination, history, canSave, handleSaveTermination]);

    return (
        <section className="main">
            {(loading || formLoading || loadingCheckApprover) && <Loader />}
            <Container fluid>
                <Row className="m-0 p-0">
                    <Col xs={10}>
                        <CandidateSelector
                            setLoading={setFormLoading}
                            data={selectedCandidate}
                            apiUrl={`${eccApiUrl}/employee/current`}
                            getDefaultSelectedAllocation={() => []}
                            onCandidateChanged={({candidateId, candidate, employmentType}) => {
                                setSelectedCandidate({
                                    candidateId: candidateId,
                                    candidate: candidate,
                                });

                                setEmploymentType(employmentType);
                            }}
                            onSelectedAllocationHistoryChanged={(selectedRows) => {
                                setSelectedAllocationHistoryRows(selectedRows);
                                setRefreshItemInEditForHistory(true);
                            }}
                            disabled={false}
                        />
                        <Row className="m-0 p-0">
                            <Col xs={12} className="m-0 p-0">
                                <ButtonGroup size="sm">
                                    <TooltipButton
                                        id={eccFormType}
                                        title="ECCF List"
                                        className="btn-icon mb-2"
                                        name={eccFormType}
                                        active={formType === eccFormType}
                                        value={eccFormType}
                                        onClick={() => {
                                            setFormType(eccFormType);
                                        }}
                                    >
                                        <i className="fas fa-th-list pt-1"></i> ECCF
                                    </TooltipButton>
                                    <TooltipButton
                                        id={terminationFormType}
                                        title="Termination List"
                                        className="btn-icon mb-2"
                                        name={terminationFormType}
                                        active={formType === terminationFormType}
                                        value={terminationFormType}
                                        onClick={() => {
                                            setFormType(terminationFormType);
                                        }}
                                    >
                                        <i className="fas fa-th-list pt-1"></i> Exit Form
                                    </TooltipButton>
                                </ButtonGroup>
                            </Col>
                        </Row>
                    {
                        formType === eccFormType ?
                        <ECCBaseForm
                            onLoadData={() => setReloadEccf(false)}
                            shouldReload={reloadEccf}
                            lookups={eccLookups}
                            setLookups={setEccLookups}
                            isApproval={false}
                            approvalData={null}
                            itemInEdit={itemInEditEccf}
                            setItemInEdit={setItemInEditEccf}
                            documents={documents}
                            setDocuments={setDocuments}
                            validationObj={validationObj}
                            setLoading={setFormLoading}
                            setShouldCheckApprover={setShouldCheckApprover}
                            selectedAllocationHistoryRows={selectedAllocationHistoryRows}
                            footer={eccFooter}
                        />
                        :
                        null
                    }
                    {
                        formType === terminationFormType ?
                        <TerminationBaseForm
                            onLoadData={() => setReloadTermination(false)}
                            reloadManagerState={!terminationApproverChecked}
                            shouldReload={reloadTermination}
                            lookups={terminationLookups}
                            setLookups={setTerminationLookups}
                            isApproval={false}
                            itemInEdit={itemInEditTermination}
                            setItemInEdit={setItemInEditTermination}
                            setLoading={setFormLoading}
                            setCanSave={setCanSave}
                            setShouldCheckApprover={setShouldCheckApprover}
                            isFinal={false}
                            validationObj={validationObj}
                            footer={terminationFooter}
                        />
                        :
                        null
                    }
                    </Col>
                    <Col xs={2} className="bg-secondary lrfstatusbox" style={{minHeight: "30vh"}}>
                    {
                        formType === eccFormType ?
                        <ECCApprovalPanel
                            itemInEdit={itemInEditEccf}
                            loading={loading}
                            initialApprover={initialApprover}
                            isApproverExists={isApproverExists}
                        />
                        :
                        null
                    }
                    {
                        formType === terminationFormType ?
                        <TerminationApprovalPanel
                            itemInEdit={itemInEditTermination}
                            loading={formLoading}
                            initialApprover={initialApprover}
                            isApproverExists={isApproverExists}
                        />
                        :
                        null
                    }
                    </Col>
                </Row>
            </Container>
        {
            popupMessageObj?.type === "confirmation" ?
            <ConfirmGeneralAlert
                onConfirm={() => formType === eccFormType ? handleSaveEcc(true) : (formType === terminationFormType ? handleSaveTermination(true) : null)}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title={`${formType === eccFormType ? "ECCF" : "Exit Form"} Submit for Approval Confirmation`}
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        {
            popupMessageObj?.type === "error" ?
            <ErrorNotification
                onConfirm={() => setPopupMessageObj(null)}
                message={popupMessageObj.message}
            />
            :
            null
        }
        </section>
    );
};